import React from 'react';
import { pickKey, type LString } from '@web-solutions/module-localization';
import { getLocalizedText } from 'core/utils/localized';
import getLocale from 'src/localization/get-locale';

import { PrimaryButton } from './PrimaryButton';
import styles from './SpecialOfferPopup.module.scss';

/** Same values as `specialOffer.mode` on the FoodZee paywalls */
export type SpecialOfferPopupMode = 'info' | 'with_plans' | 'with_plans_v2';

/**
 * The `specialOffer` remote-config object. Every text in the sheet comes from
 * it and nothing else: a field that is missing or empty for the user's language
 * is simply not rendered (no built-in fallback copy).
 */
export type SpecialOfferPopupConfig = {
  /** `info` / empty = gift + headline + CTA; `with_plans` (or `with_plans_v2`) adds the plan card */
  mode?: SpecialOfferPopupMode | string;
  discount?: number | string;
  /** Headline; `\n` = line break, `{discount}` = the percent */
  title?: LString;
  /** Words of `title` drawn with the pink→violet gradient (case-insensitive, `{discount}` allowed).
   *  Empty → every line after the first gets the gradient */
  highlightWords?: string[];
  /** CTA label */
  button?: LString;
  /** with_plans: line under the plan name, `{price}` = price after trial */
  thenText?: LString;
  /** Reserved for the countdown line (`{time}`); the sheet does not render a timer yet */
  timerText?: LString;
};

type Props = {
  isOpen: boolean;
  config?: SpecialOfferPopupConfig;
  /** Special-offer product the payment window will open with (with_plans) */
  product?: any;
  /** CTA — reopen the payment window with the special-offer product */
  onContinue: () => void;
  /** Tap on the dimmed background — dismiss without opening the payment window */
  onClose: () => void;
};

// Config text for the current language with `{name}` placeholders filled in.
const text = (value: LString | undefined, vars: Record<string, string | number> = {}): string =>
  Object.entries(vars).reduce(
    (acc, [k, v]) => acc.split(`{${k}}`).join(String(v)),
    (pickKey(value) || '').trim(),
  );

// What is charged today for a product: the config's own billedAmount wins,
// the mapped trialPrice is used only when it is a real non-zero amount (same
// rule as priceTerms in the paywall).
const todayPrice = (p: any): string => {
  const mapped = Number(String(p?.trialPrice ?? '').replace(/[^\d.]/g, ''));
  return p?.priceStartTrial?.billedAmount || (mapped > 0 ? p.trialPrice : '') || '';
};

const escapeRegExp = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

// Headline: `\n` → line break. Every line after the first is drawn with the
// gradient ("You’re getting an exclusive\n75% discount"); `highlightWords`
// additionally colour the listed words inside the first line.
const renderTitle = (title: string, highlightWords: string[]) => {
  const words = highlightWords.filter(Boolean);
  const regex = words.length ? new RegExp(`(${words.map(escapeRegExp).join('|')})`, 'gi') : null;
  // a "\n" typed literally in Magnus works the same as a real line break
  return title.replace(/\\n/g, '\n').split('\n').map((line, i) => (
    <React.Fragment key={i}>
      {i > 0 && <br />}
      {i > 0 ? (
        <span className={styles.discount}>{line}</span>
      ) : regex ? (
        line.split(regex).map((part, j) =>
          words.some((w) => w.toLowerCase() === part.toLowerCase()) ? (
            <span key={j} className={styles.discount}>{part}</span>
          ) : (
            part
          )
        )
      ) : (
        line
      )}
    </React.Fragment>
  ));
};

// Bottom sheet shown after the user closed the payment window (`showSpecialOffer`).
// The prices themselves switch to `specialOffer.products` on close (see
// onPaymentClose in the paywall); this sheet only tells the user about it.
//  - info (default): gift + headline + CTA
//  - with_plans:     + the special-offer plan card (name, price after trial, price today)
export const SpecialOfferPopup: React.FC<Props> = ({ isOpen, config, product, onContinue, onClose }) => {
  const locale = getLocale();

  if (!isOpen) return null;

  const { mode, title, highlightWords, button, thenText } = config || {};
  const discount = Number(config?.discount) || 0;
  const withPlans = (mode === 'with_plans' || mode === 'with_plans_v2') && !!product;

  const headline = text(title, { discount });
  const highlight = (highlightWords || []).map((w) => String(w).split('{discount}').join(String(discount)));
  const cta = text(button);

  const planTitle =
    getLocalizedText(product?.title, locale) || getLocalizedText(product?.productTitle, locale);
  const afterTrial =
    getLocalizedText(product?.priceStartTrial?.price, locale) || product?.priceText || product?.price || '';
  const planSub = afterTrial ? text(thenText, { price: afterTrial }) : '';
  const planPrice = todayPrice(product);

  return (
    <>
      <div className={styles.overlay} onClick={onClose} />
      <div className={`${styles.sheet} ${withPlans ? styles.sheetPlans : ''}`} role="dialog" aria-modal="true">
        <div className={styles.gift} />

        {headline && <h3 className={styles.title}>{renderTitle(headline, highlight)}</h3>}

        {withPlans && (
          <div className={styles.plan}>
            <div className={styles.planText}>
              {planTitle && <p className={styles.planTitle}>{planTitle}</p>}
              {planSub && <p className={styles.planSub}>{planSub}</p>}
            </div>
            {planPrice && <span className={styles.planPrice}>{planPrice}</span>}
          </div>
        )}

        {cta && (
          <PrimaryButton sticky={false} onClick={onContinue}>
            {cta}
          </PrimaryButton>
        )}
      </div>
    </>
  );
};

export default SpecialOfferPopup;
